import React, { useEffect, useContext } from 'react';
import { useDispatch } from 'react-redux';

import {
  TimezoneSelect,
  PartnersSelect,
  VehiclesGroupsSelect,
  ReservationsRulesSelect,
} from 'components/AsyncFields';
import { UserContext } from 'context';
import i18n from 'i18n-js';
import * as Yup from 'yup';
import { FormChanges } from 'utils';
import { Formik, Field } from 'formik';
import useStyles from './Details.styled';
import { TextField } from 'components/FormFields';
import { Box, Grid, Container, Typography } from '@material-ui/core';
import { VehiclesGroupsActions } from 'state/modules/vehiclesGroups';

const CustomersGroupsDetails = ({ formRef, errors, step, exit, onSave, rules }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { orgType } = useContext(UserContext);

  const initialValues = {
    name: step?.name || '',
    label: step?.label || '',
    time_zone: step?.time_zone || '',
    parent_id: step?.parent?.id || '',
    organization_id: step?.parent?.organization_id || '',
    rules_set_id: step?.rules_set_id || undefined,
  };

  const formSchema = Yup.object().shape({
    name: Yup.string().required(i18n.t('common.required_field')),
    label: Yup.string().required(i18n.t('common.required_field')),
    time_zone: Yup.string().required(i18n.t('common.required_field')),
    parent_id: Yup.number().required(i18n.t('common.required_field')),
    ...(orgType === 'msServiceOrg'
      ? { organization_id: Yup.string().required(i18n.t('common.required_field')) }
      : {}),
    rules_set_id: Yup.number(),
  });

  useEffect(() => {
    if (formRef.current && rules?.length === 1) {
      if (rules?.length === 1 && !formRef?.current?.values?.rules_set_id) {
        formRef.current.setFieldValue('rules_set_id', rules[0]?.id);
      }
    }
  }, [rules, formRef]);

  return (
    <Box className={classes.container}>
      <Container className="h-100">
        <Grid container direction="row" justifyContent="center" alignItems="center">
          <Grid item xs={12}>
            <Formik
              enableReinitialize
              innerRef={formRef}
              initialValues={initialValues}
              validationSchema={formSchema}
              onSubmit={(values) => {
                let payload = {};
                if (step?.id) {
                  payload = { ...values };
                } else {
                  payload = {
                    ...values,
                    group_type: 'CUSTOMER',
                  };
                }
                delete payload?.organization_id;
                if (payload?.rules_set_id === '') delete payload?.rules_set_id;
                dispatch(VehiclesGroupsActions.fetchCreateVehiclesGroups(payload, 'details', exit));
              }}
            >
              {({ values, setFieldValue, handleSubmit }) => (
                <form noValidate onSubmit={handleSubmit}>
                  <Grid
                    container
                    spacing={2}
                    direction="row"
                    alignItems="flex-start"
                    justifyContent="flex-start"
                  >
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        <Box fontWeight="fontWeightBold" fontSize={14} my={1}>
                          {i18n.t('customer_groups.general_info')}
                        </Box>
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        <Box fontSize={12} my={1}>
                          {i18n.t('customer_groups.general_info_description')}
                        </Box>
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <Field
                        type="text"
                        name="name"
                        component={TextField}
                        asyncErrors={errors?.name}
                        label={i18n.t('customer_groups.internal_name')}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <Field
                        type="text"
                        name="label"
                        component={TextField}
                        asyncErrors={errors?.label}
                        label={i18n.t('customer_groups.customer_label')}
                      />
                    </Grid>
                    {orgType === 'msServiceOrg' ? (
                      <>
                        <Grid item xs={12} sm={6} md={6}>
                          <Field
                            name="organization_id"
                            component={PartnersSelect}
                            label={i18n.t('customer_groups.fleet_owner')}
                            onChange={(value) => {
                              if (value?.id !== values?.organization_id) {
                                setFieldValue('parent_id', '');
                              }
                            }}
                            customParams={{ status: ['ACTIVE', 'CANREQUESTED'] }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={6}>
                          <Field
                            name={'parent_id'}
                            component={VehiclesGroupsSelect}
                            disabled={!values.organization_id}
                            customParams={{
                              organization_id: values.organization_id,
                            }}
                            label={i18n.t('customer_groups.fleet')}
                          />
                        </Grid>
                      </>
                    ) : (
                      <>
                        <Grid item xs={12} sm={6} md={6}>
                          <Field
                            name={'parent_id'}
                            component={VehiclesGroupsSelect}
                            customParams={{ group_type: 'OWNER' }}
                            label={i18n.t('customer_groups.vehicle_group')}
                          />
                        </Grid>
                      </>
                    )}

                    <Grid item xs={12} sm={6} md={6}>
                      <Field
                        name="time_zone"
                        component={TimezoneSelect}
                        label={i18n.t('customer_groups.timezone')}
                      />
                    </Grid>
                    {rules?.length > 1 && (
                      <Grid item xs={12} sm={6} md={6}>
                        <Field
                          type="driver"
                          name="rules_set_id"
                          id="DriversReservationRules"
                          options={rules?.driver || []}
                          component={ReservationsRulesSelect}
                          label={i18n.t('vehicle_groups.reservation_ruleset')}
                        />
                      </Grid>
                    )}
                  </Grid>
                  <FormChanges initialValues={initialValues} onSave={onSave} />
                </form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default CustomersGroupsDetails;
